import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import classnames from "classnames";

import {
  BusinessRole,
  MaterialCategoryName,
  MaterialGroup,
  MaterialGroupByRegYear,
} from "fe-shared/src/types/domain";
import useMaterialsByCode from "fe-shared/src/hooks/useMaterialsByCode";
import useDialog from "fe-shared/src/hooks/useDialog";
import { handleKeyDown } from "fe-shared/src/helpers/buttonHandlers";

import {
  isTransaction,
  isTransactionWithWeightAndScale,
} from "../../helpers/transactions";
import useEstimatedWeight from "../../hooks/useEstimatedWeight";

import {
  InfoRow,
  Transaction,
  TransactionMaterial,
  TransactionTraversedMaterial,
  TransactionType,
  Transfer,
} from "../../types";

import {
  AttributesInfo,
  DetailsInfoBlock,
  DetailsModal,
  ScaleTicketsInfo,
} from "..";

import styles from "./DetailsDataSection.module.scss";
import getMaterialGroupsByRegYear from "../../helpers/getMaterialGroupsByRegYear";

interface Props {
  transactionOrTransfer: Transaction | Transfer;
  parentTransactionType?: TransactionType;
  showMaterialsByRegYear?: boolean;
  showComparisonMaterials?: boolean;
}

const TransactionDataInfo = ({
  transactionOrTransfer,
  parentTransactionType,
  showMaterialsByRegYear,
  showComparisonMaterials,
}: Props) => {
  const { t } = useTranslation();
  const {
    materialPhotos = [],
    paperFormImages = [],
    comment,
    grossWeight,
    tareWeight,
    traversedMaterials,
  } = transactionOrTransfer;
  const materials = transactionOrTransfer.materials as TransactionMaterial[];
  const isWithTires =
    transactionOrTransfer.materials[0]?.materialCategoryName ===
    MaterialCategoryName.Tires;
  const materialsByCode = useMaterialsByCode();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const {
    isDialogRendered,
    isDialogActive,
    openDialog,
    closeDialog,
  } = useDialog();
  const photos = [...paperFormImages, ...materialPhotos];

  const {
    translationRoot,
    scaleTickets,
    type,
    isAdjustment,
    isWeightTicketsInfo,
  } = useMemo(
    () =>
      isTransaction(transactionOrTransfer)
        ? {
            translationRoot: "transactions",
            scaleTickets: transactionOrTransfer.scaleTickets,
            type: transactionOrTransfer.transactionType,
            isAdjustment:
              transactionOrTransfer.transactionType ===
              TransactionType.Adjustment,
            isWeightTicketsInfo: isTransactionWithWeightAndScale(
              transactionOrTransfer
            ),
          }
        : {
            translationRoot: "transfers",
            scaleTickets: [],
            type: transactionOrTransfer.transferType,
            isAdjustment: false,
            isWeightTicketsInfo: false,
          },
    [transactionOrTransfer]
  );

  const displayType = parentTransactionType || type;

  const onCloseDialog = () => {
    setSelectedImage(null);
    closeDialog();
  };

  useEffect(() => {
    if (selectedImage) {
      openDialog();
    }
  }, [selectedImage, openDialog]);

  const isRegYearEnabled: boolean =
    !!showMaterialsByRegYear &&
    !!traversedMaterials &&
    !!traversedMaterials.length;

  const materialsTitle = useMemo(
    () =>
      isTransaction(transactionOrTransfer) && isAdjustment
        ? transactionOrTransfer.linkedTransactions
            .map((transaction) =>
              t(
                `${translationRoot}.details.${transaction.transactionType}.materialsTitle`
              )
            )
            .join("/")
        : t(`${translationRoot}.details.${displayType}.materialsTitle`),
    [displayType, isAdjustment, t, transactionOrTransfer, translationRoot]
  );

  const materialsInfo = useMemo(() => {
    const units = isWithTires ? "" : t("units.Kg");

    return [
      {
        key: materialsTitle,
        label: materialsTitle,
        value: `${materials
          .reduce((sum, m) => sum + m.units.unitQuantity, 0)
          .toLocaleString()} ${units}`,
        type: "title",
      },
      ...materials.map((item) => ({
        key: item.materialCode,
        label: materialsByCode[item.materialCode]?.name,
        value: `${item.units.unitQuantity?.toLocaleString()} ${units}`,
        type: "row",
      })),
    ];
  }, [isWithTires, t, materialsTitle, materials, materialsByCode]);

  const estimatedWeight = useEstimatedWeight(materials, [
    BusinessRole.Hauler,
    BusinessRole.Collector,
    BusinessRole.Processor,
    BusinessRole.Producer,
    BusinessRole.OneTimePickup,
  ]);

  const weightInfo = useMemo(
    () => [
      {
        label: t("transactions.details.weights.netWeight"),
        value: `${(grossWeight - tareWeight)?.toLocaleString()} ${t(
          "units.Kg"
        )}`,
        type: "title",
      },
      ...(isWithTires
        ? [
            {
              label: t("transactions.details.weights.estimatedWeight"),
              value: `${estimatedWeight?.toLocaleString()} ${t("units.Kg")}`,
              type: "subtitle",
            },
          ]
        : []),
      {
        label: t("transactions.details.weights.grossWeight"),
        value: `${grossWeight?.toLocaleString()} ${t("units.Kg")}`,
        type: "row",
      },
      {
        label: t("transactions.details.weights.tareWeight"),
        value: `${tareWeight?.toLocaleString()} ${t("units.Kg")}`,
        type: "row",
      },
    ],
    [t, grossWeight, tareWeight, isWithTires, estimatedWeight]
  );

  const traversedMaterialsByRegYear = useMemo(
    () => getMaterialGroupsByRegYear(traversedMaterials),
    [traversedMaterials]
  );

  const weightInfoByRegYear = useMemo(
    () =>
      traversedMaterialsByRegYear.map((r) => ({
        key: String(r.regYear),
        label: t("transactions.details.netWeightInRegYear", {
          regYear: r.regYear,
        }),
        value: `${Math.floor(r.weight).toLocaleString()} kg`,
        type: "title",
      })),
    [t, traversedMaterialsByRegYear]
  );

  const rowsByRegYearInfo = useMemo(
    () =>
      traversedMaterialsByRegYear.map(
        (
          groupsByRegYear: MaterialGroupByRegYear<TransactionTraversedMaterial>
        ) => {
          const info: InfoRow[] = groupsByRegYear.materialGroups
            .map(
              (g: MaterialGroup<TransactionMaterial>) =>
                [
                  {
                    key: materialsTitle,
                    label: materialsTitle,
                    value: (g.total || 0).toLocaleString(),
                    type: "title",
                  },
                  ...g.items.map((i: TransactionMaterial) => ({
                    key: i.materialCode,
                    label: i.materialName,
                    value: (i.units.unitQuantity || 0).toLocaleString(),
                    type: "row",
                  })),
                ] as InfoRow[]
            )
            .flat();

          return (
            <Fragment key={`${groupsByRegYear.regYear}-group-reg-year`}>
              <p className={styles.regYearTitle}>
                {groupsByRegYear.regYear
                  ? t("transfers.details.regYear", {
                      regYear: groupsByRegYear.regYear,
                    })
                  : t("transfers.details.unidentifiedRegYear")}
              </p>

              <DetailsInfoBlock rows={info} />
            </Fragment>
          );
        }
      ),
    [materialsTitle, t, traversedMaterialsByRegYear]
  );

  return (
    <>
      <div className={styles.tiresInformation}>
        {(!isRegYearEnabled || showComparisonMaterials) && (
          <span
            className={classnames({
              [styles.deprecated]: showComparisonMaterials,
            })}
          >
            <DetailsInfoBlock rows={materialsInfo} />
          </span>
        )}
        {isRegYearEnabled && rowsByRegYearInfo}
        {isWithTires && (
          <AttributesInfo transactionOrTransfer={transactionOrTransfer} />
        )}
        {!isAdjustment && !!photos.length && (
          <div className={styles.tiresPicWrapper}>
            {photos.map((item) => {
              const onClick = () => setSelectedImage(item);

              return (
                <div
                  key={item}
                  role="button"
                  tabIndex={0}
                  className={styles.tirePic}
                  onClick={() => setSelectedImage(item)}
                  onKeyDown={(e) => handleKeyDown(e, onClick)}
                >
                  <img src={item} alt="" />
                </div>
              );
            })}
          </div>
        )}
        {!!comment && <p>{comment}</p>}
      </div>
      <div className={styles.tiresInformation}>
        {isWeightTicketsInfo && (
          <>
            <DetailsInfoBlock rows={weightInfo} />
            {!!scaleTickets?.length && (
              <ScaleTicketsInfo
                scaleTickets={scaleTickets}
                onClick={setSelectedImage}
                isAdjustment={isAdjustment}
              />
            )}
            {isRegYearEnabled && (
              <>
                <div className={styles.separator} />
                <DetailsInfoBlock
                  rowClassName={styles.weightByRegYear}
                  rows={weightInfoByRegYear}
                />
              </>
            )}
          </>
        )}
      </div>
      {isDialogRendered && (
        <DetailsModal
          selectedImage={selectedImage}
          isDialogActive={isDialogActive}
          onCloseDialog={onCloseDialog}
          materialsInfo={materialsInfo}
          weightInfo={weightInfo}
          isWeightTicketsInfo={isWeightTicketsInfo}
          transactionOrTransfer={transactionOrTransfer}
        />
      )}
    </>
  );
};

export default TransactionDataInfo;
